/**
 * Office days (Employee rebuild C4) — the shapes the server sends and the few
 * words the screens put on them.
 *
 * 🔴 NOTHING HERE COUNTS. Verified, required, excused and "how it's counted"
 * all come from GET /hybrid/compliance/me, which asks the same function the
 * manager's Compliance Report does. The page renders them; it never re-derives
 * a number — a page that restates a rule is a page that disagrees with it.
 */
import { display } from '@/utils/dateDisplay';
import { apiGet } from '@/utils/api';

export type DayType = 'office' | 'remote' | 'leave' | 'half_day_leave' | 'client_site';

/** The editor's buttons, in the prototype's order. */
export const DAY_TYPES: { key: DayType; label: string }[] = [
  { key: 'office', label: 'Office' },
  { key: 'remote', label: 'Remote' },
  { key: 'leave', label: 'Leave' },
  { key: 'half_day_leave', label: 'Half-day' },
  { key: 'client_site', label: 'Client site' },
];

export const DAY_TYPE_LABEL: Record<string, string> = {
  office: 'Office', remote: 'Remote', leave: 'Leave', half_day_leave: 'Half-day leave',
  client_site: 'Client site', not_in_office: 'Not in office',
};

export const WEEKDAYS = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
] as const;

export interface ScheduleRow {
  id: string;
  week_start: string;
  days: string[] | null;
  day_types: Record<string, DayType> | null;
  status: string;
  approver_name: string | null;
  decider_name: string | null;
  decision_note: string | null;
  week_state: string;
  locked_days?: string[];
  can_edit: boolean;
  blocked_reason: string | null;
}

export interface MyScheduleEnvelope {
  data: ScheduleRow[];
  team_name: string | null;
  team_preferred_days: string[] | null;
  approver_name: string | null;
  routing: string;
  can_submit: boolean;
}

export interface MyPolicyAnswer {
  policy: { id: string; name: string; min_office_days?: number | null } | null;
  sentence: string;
}

export interface AttendanceDay {
  date: string;
  check_in_at: string | null;
  method: 'qr' | 'app' | null;
  counted: boolean;
  excused: number;
  kind: string;
  holiday: string | null;
  planned: string | null;
  dispute: { id: string; status: string; outcome: string | null } | null;
  disputable: boolean;
}

export interface MyCompliance {
  today: string;
  week: {
    start: string; end: string; label: string; policy_name: string | null;
    min_office_days: number | null; working_days: number; normal_days: number;
    required_days: number | null; verified_days: number; excused_days: number;
    planned_days_left: number; status: string; status_label: string;
  };
  month: {
    start: string; end: string; label: string; required_days: number | null;
    verified_days: number; excused_days: number; pct: number | null;
    booked_not_checked_in: number; no_shows: number; status: string; status_label: string;
  };
  days: AttendanceDay[];
  rules: {
    office_day_requires_qr: boolean; client_site_counts_as_office: boolean;
    normal_days: number; dispute_window_days: number;
  };
  how_counted: string;
  holidays: { date: string; name: string }[];
}

export interface TeamWeekPerson {
  user_id: string;
  user_name: string | null;
  days: string[] | null;
  day_types: Record<string, string> | null;
  shared: boolean;
}

export interface TeamWeek {
  week_start: string;
  week_state: string;
  teams: { team_name: string; preferred_days: string[] | null; manager_name?: string | null }[];
  me: { user_id: string; user_name: string | null; day_types: Record<string, string> | null;
        status: string | null };
  people: TeamWeekPerson[];
}

/** "Tue", "Tue and Wed", "Tue, Wed and Thu" — a list read aloud, not joined
 *  with " & " each time (Employee R2, E2E observation 5). */
export function naturalList(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

export interface MyHolidays {
  year: number;
  building_name: string | null;
  building_city: string | null;
  business_days: string[];
  calendar_source: string;
  holidays: { date: string; name: string; on_business_day: boolean; past: boolean }[];
}

export const QK_MY_COMPLIANCE = ['hybrid', 'compliance', 'me'];
export const QK_MY_SCHEDULE_OD = ['hybrid', 'my-schedule'];
export const QK_TEAM_WEEK = ['hybrid', 'team-week'];
export const QK_MY_HOLIDAYS = ['hybrid', 'holidays', 'me'];
export const QK_MY_POLICY_OD = ['hybrid', 'policies', 'mine'];
export const QK_MY_DISPUTES = ['hybrid', 'disputes', 'mine'];
/** The manager's queue (components/Hybrid/ScheduleApprovals) — a withdrawal
 *  takes a week out of it. */
export const QK_SCHEDULE_APPROVALS = ['hybrid', 'schedule-approvals'];

/** How many weeks My week reads: this one and the eight a plan may reach. */
export const MY_SCHEDULE_WEEKS = 9;

/**
 * THE read of my weeks, for every screen that shows them — Office days › My
 * week, My Day's "This week" and its "Today:" chip, the first-login welcome.
 *
 * 🔴 ONE QUERY, ONE CACHE ENTRY (Employee fix, bug 4). My Day used to ask for
 * `weeks=1` under its own key while Office days asked for `weeks=9`, so the
 * two screens held two copies of this week that could be a refetch apart, and
 * each mapped the row to day states its own way. Sharing the key means a
 * change made on either screen (and its optimistic update) is the same data
 * on both.
 */
export const myScheduleQuery = {
  queryKey: [...QK_MY_SCHEDULE_OD, MY_SCHEDULE_WEEKS],
  queryFn: () => apiGet<MyScheduleEnvelope>(`/hybrid/my-schedule?weeks=${MY_SCHEDULE_WEEKS}`),
  staleTime: 30_000,
};

/**
 * What one weekday of a week is planned as, or null when it is not planned.
 *
 * 🔴 THE SERVER'S day_types IS THE WHOLE ANSWER (Employee fix, bug 4). It is
 * already the effective map — a legacy office-days-only row comes back with
 * its other days as remote (services/personal_schedule.effective_day_types) —
 * so a day missing from it was never declared. My Day used to fall back to
 * `days` and call every non-office day "remote": with only Friday marked
 * Remote, `days` is [] and Monday to Thursday all read Remote there while
 * Office days showed them as "—". Both screens call this, so they cannot
 * disagree again.
 */
export function plannedDayType(row: Pick<ScheduleRow, 'day_types'> | null | undefined,
                               weekday: string): DayType | null {
  return row?.day_types?.[weekday] ?? null;
}

/** "yyyy-mm-dd" plus n days, as a calendar date — never through a local
 *  Date at midnight (noNaiveDateParsing). */
export function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const t = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1) + n * 86_400_000);
  return t.toISOString().slice(0, 10);
}

/** Monday = 0 for a calendar date. */
export function weekdayIndex(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return (new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)).getUTCDay() + 6) % 7;
}

export function titleDay(day: string): string {
  return day.charAt(0).toUpperCase() + day.slice(1, 3);
}

/** "19–23 Oct" for a Monday, the five weekdays the prototype shows. */
export function weekRange(monday: string, length = 5): string {
  const end = addDays(monday, length - 1);
  const a = display(monday, 'dayMonth');
  const b = display(end, 'dayMonth');
  const [ad, am] = a.split(' ');
  const [bd, bm] = b.split(' ');
  return am === bm ? `${ad}–${bd} ${bm}` : `${a} – ${b}`;
}

/** The words for one day of My check-ins, from the server's `kind`. */
export const KIND_WORDS: Record<string, string> = {
  verified: 'Office day',
  client_site: 'Client site (counts)',
  dispute_verified: 'Office day (dispute accepted)',
  not_counted_app: 'In-app check-in (QR required)',
  leave: 'Approved leave',
  half_day_leave: 'Half-day leave',
  dispute_excused: 'Excused (dispute accepted)',
  no_show: 'Booked, no check-in',
  booked: 'Booked',
  holiday: 'Public holiday',
  remote: 'Remote',
  not_in_office: 'Not in office',
};

/** The Counted column. */
export function countedWords(d: AttendanceDay): string {
  if (d.counted) return '1 day';
  if (d.excused === 1) return 'Excused';
  if (d.excused > 0) return 'Half excused';
  if (d.kind === 'no_show') return '0 · no-show';
  if (d.kind === 'not_counted_app') return '0 · not QR';
  return '—';
}

/** A day worth a row in My check-ins: something happened or was recorded. */
export function isEventful(d: AttendanceDay): boolean {
  return d.counted || d.excused > 0 || d.check_in_at !== null
    || d.kind === 'no_show' || d.kind === 'booked' || d.dispute !== null;
}
